// useEmailVerification.js
// Custom hook for managing email verification process

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../services/firebase';
import { applyActionCode, reload, sendEmailVerification } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

const useEmailVerification = () => {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const mode = params.get('mode');
  const oobCode = params.get('oobCode');
  
  const [status, setStatus] = useState('init'); // init | ready | applying | success | error | invalid | resent
  const [errorMsg, setErrorMsg] = useState('');

  // Redirect to appropriate page based on mode
  useEffect(() => {
    if (mode === 'resetPassword') {
      // Redirect to password reset page with all params
      navigate(`/reset-password?${params.toString()}`, { replace: true });
    }
  }, [mode, navigate, params]);

  // Create user profile document if missing
  const createUserProfile = async (user) => {
    const userRef = doc(db, 'users', user.uid);
    const snap = await getDoc(userRef);
    
    if (!snap.exists()) {
      await setDoc(userRef, {
        name: user.displayName || '',
        email: user.email,
        joinedAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        profilePic: user.photoURL || '',
        emailVerified: true
      });
    }
  };

  // Apply verification code
  useEffect(() => {
    if (!oobCode || mode !== 'verifyEmail') {
      setStatus('invalid');
      return;
    }

    const verifyEmail = async () => {
      setStatus('applying');
      
      try {
        await applyActionCode(auth, oobCode);
        
        if (auth.currentUser) {
          await reload(auth.currentUser);
          await createUserProfile(auth.currentUser);
        }
        
        setStatus('success');
        setTimeout(() => navigate('/market'), 2000);
      } catch (err) {
        // Check if user is already verified (common with email scanners)
        if (auth.currentUser) {
          await reload(auth.currentUser);
          if (auth.currentUser.emailVerified) {
            // Email already verified - show success!
            setStatus('alreadyVerified');
            setTimeout(() => navigate('/market'), 3000);
            return;
          }
        }
        
        // Customize error message for already used/expired links
        const errorCode = err.code;
        let friendlyMessage = err.message || 'Invalid or expired verification link.';
        
        if (errorCode === 'auth/invalid-action-code' || errorCode === 'auth/expired-action-code') {
          friendlyMessage = 'This verification link has expired or was already used. Your email may already be verified, or you can request a new link.';
        }
        
        setStatus('error');
        setErrorMsg(friendlyMessage);
      }
    };

    verifyEmail();
  }, [oobCode, mode, navigate]);

  // Resend verification email
  const resendEmail = async () => {
    if (!auth.currentUser) {
      navigate('/login');
      return;
    }

    try {
      await sendEmailVerification(auth.currentUser, {
        url: 'https://redbirdmarkets.com/verify-email',
        handleCodeInApp: true
      });
      setStatus('resent');
    } catch (e) {
      setErrorMsg(e.message || 'Failed to resend.');
    }
  };

  // Retry verification
  const retryVerification = () => {
    window.location.reload();
  };

  // Navigate to login
  const goToLogin = () => {
    navigate('/login');
  };

  return {
    status,
    errorMsg,
    resendEmail,
    retryVerification,
    goToLogin
  };
};

export { useEmailVerification };
export default useEmailVerification;